/**
 * Central API access for the payment page.
 *
 * Historically every page hardcoded `https://api.pouchfinance.xyz` (and one used
 * a relative `/api/...` path that resolved to the wrong origin). This routes all
 * calls through a single base that honors VITE_API_URL, so staging/local work
 * and there's one host to change.
 */
export const API_BASE = (import.meta.env.VITE_API_URL || 'https://api.pouchfinance.xyz').replace(/\/+$/, '');

export function apiUrl(path: string): string {
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

// ─── Typed API contracts ────────────────────────────────────────────────────
// Mirror of @pouchpay/sdk's SessionResponse (packages/sdk/src/types.ts). Kept
// here as a local copy so the payment page has no runtime SDK dependency, but
// typing GET /v2/sessions/:id against it turns status/field-name drift (e.g.
// PAYMENT_PENDING vs PENDING_PAYMENT, paymentInstruction vs virtualAccount)
// into compile errors instead of blank screens. Keep in sync with the SDK type.

export interface PaymentInstruction {
  accountNumber?: string;
  accountName?: string;
  bankName?: string;
  amountUsd?: number;
  amountLocal?: number;
  localCurrency?: string;
  fxRate?: number;
  cryptoAmount?: number;
  cryptoCurrency?: string;
  cryptoNetwork?: string;
  fees?: { networkFee?: number; serviceFee?: number; totalFees?: number };
  reference?: string;
  expiresAt?: string | null;
}

export interface CryptoInstruction {
  walletAddress?: string;
  cryptoNetwork?: string;
  cryptoCurrency?: string;
  cryptoAmount?: number;
  amountUsd?: number;
  amountLocal?: number;
  localCurrency?: string;
  fxRate?: number;
  fees?: { networkFee?: number; serviceFee?: number; totalFees?: number };
  reference?: string;
  expiresAt?: string | null;
}

export interface TransactionSummary {
  status: string;
  amountLocal: number;
  localCurrency: string;
  amountUsd?: number | null;
  cryptoAmount?: number | null;
  cryptoCurrency?: string | null;
  fxRate: number;
  settledAt?: string | null;
}

/** GET /v2/sessions/:id — mirrors @pouchpay/sdk SessionResponse. */
export interface SessionResponse {
  id: string;
  type: 'ONRAMP' | 'OFFRAMP';
  status: string;
  countryCode?: string;
  emailVerified: boolean;
  kycVerified: boolean;
  bankAccountProvided: boolean;
  paymentInstruction?: PaymentInstruction;
  cryptoInstruction?: CryptoInstruction;
  transaction?: TransactionSummary;
  providerStatus?: string;
  txHash?: string;
  expiresAt: string;
  metadata?: Record<string, unknown> | null;
}

/** Fetch a session, typed. Throws on non-2xx. */
export async function getSession(sessionId: string, token?: string): Promise<SessionResponse> {
  const res = await fetch(apiUrl(`/v2/sessions/${sessionId}`), {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!res.ok) throw new Error('Failed to fetch session');
  return res.json() as Promise<SessionResponse>;
}

export interface RateQuote {
  /** Crypto/USD amount the user receives (crypto here is USD-pegged stablecoin). */
  cryptoAmount: number;
  /** Local currency per 1 USD. */
  rate: number;
  feePercent?: number;
}

/**
 * Fetch a display quote from the existing session-less rate endpoint
 * (GET /crypto/rate). onramp → BUY, offramp → SELL. `fiatCurrency` must be a
 * 3-letter ISO code (e.g. NGN), NOT the 'local'|'crypto' URL param.
 */
export async function fetchRateQuote(p: {
  type: 'onramp' | 'offramp';
  amount: string | number;
  fiatCurrency: string;
  countryCode: string;
}): Promise<RateQuote> {
  const qs = new URLSearchParams({
    amount: String(p.amount),
    fromCurrency: p.fiatCurrency,
    toCurrency: 'USD',
    type: p.type === 'offramp' ? 'SELL' : 'BUY',
    countryCode: p.countryCode,
  });
  const res = await fetch(apiUrl(`/crypto/rate?${qs.toString()}`));
  if (!res.ok) throw new Error('Failed to fetch quote');
  const data = await res.json();
  return { cryptoAmount: Number(data.toAmount), rate: Number(data.rate) };
}
